import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import { Close as CloseIcon, Favorite as FavoriteIcon, Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { addToWishlistAsync, selectWishlistLoading } from '../store/wishlistSlice';
import AsyncSelect from 'react-select/async';
import { searchProducts } from '../../product/services/productService';
import { fetchSuppliers } from '../../../app/store/slices/supplierSlice';
import AddProductDialog from '../../product/components/AddProductDialog';

/**
 * Enhanced "Add to Wishlist" dialog.
 *
 * Supports two modes:
 * 1. Existing Product: Search and select from database
 * 2. Custom One-Time Product: Manual entry without saving to products
 *
 * Props:
 *   open        {boolean}
 *   onClose     {() => void}
 *   product     {object | null}  — optional pre-selected product
 *   onSuccess   {() => void}     — optional callback after successful add
 */
const AddToWishlistDialog = ({ open, onClose, product, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const loading = useSelector(selectWishlistLoading);
  const suppliersFromStore = useSelector((state) => state.suppliers.suppliers) || [];
  
  // Refs
  const debounceRef = useRef();

  // Product type: "existing" or "custom"
  const [productType, setProductType] = useState('existing');
  
  // Existing product selection
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Custom product fields
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customSupplier, setCustomSupplier] = useState(null);
  
  // Common fields
  const [quantity, setQuantity] = useState(1);
  const [priority, setPriority] = useState('Medium');
  const [notes, setNotes] = useState('');
  
  // Add product dialog
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  
  // Fetch suppliers if not in store
  useEffect(() => {
    if (open && suppliersFromStore.length === 0) {
      dispatch(fetchSuppliers());
    }
  }, [open, dispatch, suppliersFromStore.length]);

  // Reset form whenever dialog opens
  useEffect(() => {
    if (open) {
      if (product) {
        // Pre-populate with provided product
        setProductType('existing');
        const norm = {
          value: product.Id ?? product.ProductId,
          label: product.Name ?? product.ProductName ?? '',
          Id: product.Id ?? product.ProductId,
          Name: product.Name ?? product.ProductName ?? '',
          SKU: product.SKU ?? product.ProductSKU ?? '',
          SalePrice: product.SalePrice ?? product.Price ?? product.CostPrice ?? 0,
          StockQuantity: product.StockQuantity ?? product.CurrentStock ?? 0,
          SupplierId: product.SupplierId ?? product.DefaultSupplierId ?? null,
        };
        setSelectedProduct(norm);
      } else {
        setProductType('existing');
        setSelectedProduct(null);
      }
      setCustomName('');
      setCustomPrice('');
      setCustomSupplier(null);
      setQuantity(1);
      setPriority('Medium');
      setNotes('');
    }
  }, [open, product]);

  // Product search function for AsyncSelect (callback pattern like AddInvoicePage)
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.length < 2) {
        return callback([]);
      }

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (inputValue.length >= 2) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        // Handle different response structures
        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        // Limit to 50 products for performance
        const limitedProducts = productsArray.slice(0, 50);

        callback(
          limitedProducts.map((p) => ({
            value: p.Id,
            label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? ' - SKU: ' + p.SKU : ''}${p.BarCode ? ' - BarCode: ' + p.BarCode : ''}`,
            Id: p.Id,
            Name: p.Name,
            SKU: p.SKU,
            BarCode: p.BarCode,
            SalePrice: p.SalePrice,
            StockQuantity: p.StockQuantity,
            SupplierId: p.SupplierId,
          }))
        );
      } catch (error) {

        
        if (error.response?.status === 401) {
          toast.error(t('session_expired'));
        } else if (error.code === 'ERR_NETWORK') {
          toast.error(t('network_error'));
        }
        
        callback([]);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    // Validation
    if (quantity < 1) {
      return;
    }

    if (productType === 'existing') {
      if (!selectedProduct) {
        return;
      }

      // Match backend WishlistDto for existing product
      const dto = {
        ProductId: selectedProduct.Id,
        Quantity: quantity,
        Priority: priority,
        Notes: notes.trim() || null,
        SupplierId: customSupplier?.value || customSupplier?.Id || selectedProduct.SupplierId || null,
      };

      try {
        const result = await dispatch(addToWishlistAsync(dto));
        if (addToWishlistAsync.fulfilled.match(result)) {
          onSuccess?.();
          handleClose();
        } else {
          throw new Error(result.payload || 'Failed to add to wishlist');
        }
      } catch {
        // toast is handled in the slice / interceptor
      }
    } else {
      // Custom one-time product
      if (!customName.trim() || !customPrice) {
        return;
      }

      // Match backend WishlistDto for custom product
      const dto = {
        CustomProductName: customName.trim(),
        PricePerUnit: parseFloat(customPrice),
        Quantity: quantity,
        Priority: priority,
        Notes: notes.trim() || null,
        SupplierId: customSupplier?.value || customSupplier?.Id || null,
      };

      try {
        const result = await dispatch(addToWishlistAsync(dto));
        if (addToWishlistAsync.fulfilled.match(result)) {
          onSuccess?.();
          handleClose();
        } else {
          throw new Error(result.payload || 'Failed to add to wishlist');
        }
      } catch {
        // toast is handled in the slice / interceptor
      }
    }
  };

  const handleClose = () => {
    setProductType('existing');
    setSelectedProduct(null);
    setCustomName('');
    setCustomPrice('');
    setCustomSupplier(null);
    setQuantity(1);
    setPriority('Medium');
    setNotes('');
    onClose();
  };

  const handleProductAdded = () => {
    setAddProductDialogOpen(false);
    // Optionally, you could refresh the product list or auto-select the newly added product
  };

  const isValid = () => {
    if (productType === 'existing') {
      return selectedProduct && quantity >= 1;
    } else {
      return customName.trim() && customPrice && parseFloat(customPrice) > 0 && quantity >= 1;
    }
  };

  const getTotalPrice = () => {
    if (productType === 'existing' && selectedProduct) {
      return (selectedProduct.SalePrice * quantity).toFixed(2);
    } else if (productType === 'custom' && customPrice) {
      return (parseFloat(customPrice) * quantity).toFixed(2);
    }
    return '0.00';
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FavoriteIcon color="error" fontSize="small" />
              <Typography variant="h6">{t('add_to_wishlist') || 'Add to Wishlist'}</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {/* Product Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              {t('product_type') || 'Product Type'}
            </FormLabel>
            <RadioGroup
              row
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
            >
              <FormControlLabel 
                value="existing" 
                control={<Radio />} 
                label={t('existing_product') || 'Existing Product'} 
              />
              <FormControlLabel 
                value="custom" 
                control={<Radio />} 
                label={t('custom_product') || 'Custom One-Time Product'} 
              />
            </RadioGroup>
          </FormControl>

          {/* Existing Product Selection */}
          {productType === 'existing' && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 500 }}>
                  {t('search_product') || 'Search Product'}
                </Typography>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadProductOptions}
                  defaultOptions={false}
                  isClearable
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                  placeholder={t('search_product_placeholder') || 'Type to search products...'}
                  noOptionsMessage={() => t('no_products_found') || 'No products found'}
                  loadingMessage={() => t('loading') || 'Loading...'}
                  styles={{
                    container: base => ({ ...base, marginBottom: 8 }),
                    menu: base => ({ ...base, zIndex: 9999 })
                  }}
                  isDisabled={loading}
                />
              </Box>

              {selectedProduct && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{selectedProduct.Name}</Typography>
                  {selectedProduct.SKU && (
                    <Typography variant="caption" color="text.secondary">SKU: {selectedProduct.SKU}</Typography>
                  )}
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600, mt: 0.5 }}>
                    €{Number(selectedProduct.SalePrice).toFixed(2)}
                  </Typography>
                  {selectedProduct.StockQuantity > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('available_stock') || 'In stock'}: {selectedProduct.StockQuantity}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Button to add new product */}
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddProductDialogOpen(true)}
                  fullWidth
                  size="small"
                >
                  {t('or_add_new_product') || 'Or Add New Product'}
                </Button>
              </Box>
            </>
          )}

          {/* Custom Product Fields */}
          {productType === 'custom' && (
            <>
              <TextField
                autoFocus
                fullWidth
                label={t('product_name') || 'Product Name *'}
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
                required
              />

              <TextField
                fullWidth
                label={t('price_per_unit') || 'Price Per Unit *'}
                type="number"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{ mb: 2 }}
                disabled={loading}
                required
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('supplier_optional') || 'Supplier (optional)'}</InputLabel>
                <Select
                  value={customSupplier?.Id || customSupplier?.value || ''}
                  label={t('supplier_optional') || 'Supplier (optional)'}
                  onChange={(e) => {
                    const supplierId = e.target.value;
                    const supplier = suppliersFromStore.find(s => s.Id === supplierId);
                    setCustomSupplier(supplier ? { value: supplier.Id, label: supplier.Name, ...supplier } : null);
                  }}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>{t('no_supplier') || 'No Supplier'}</em>
                  </MenuItem>
                  {suppliersFromStore.map((s) => (
                    <MenuItem key={s.Id} value={s.Id}>
                      {s.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {/* Common Fields */}
          {productType === 'existing' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('supplier_optional') || 'Supplier (optional)'}</InputLabel>
              <Select
                value={customSupplier?.Id || customSupplier?.value || ''}
                label={t('supplier_optional') || 'Supplier (optional)'}
                onChange={(e) => {
                  const supplierId = e.target.value;
                  const supplier = suppliersFromStore.find(s => s.Id === supplierId);
                  setCustomSupplier(supplier ? { value: supplier.Id, label: supplier.Name, ...supplier } : null);
                }}
                disabled={loading}
              >
                <MenuItem value="">
                  <em>{t('no_supplier') || 'No Supplier'}</em>
                </MenuItem>
                {suppliersFromStore.map((s) => (
                  <MenuItem key={s.Id} value={s.Id}>
                    {s.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <TextField
            fullWidth
            label={t('quantity') || 'Quantity'}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 100000 }}
            sx={{ mb: 2 }}
            disabled={loading}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('priority') || 'Priority'}</InputLabel>
            <Select
              value={priority}
              label={t('priority') || 'Priority'}
              onChange={(e) => setPriority(e.target.value)}
              disabled={loading}
            >
              <MenuItem value="Low">{t('low') || 'Low'}</MenuItem>
              <MenuItem value="Medium">{t('medium') || 'Medium'}</MenuItem>
              <MenuItem value="High">{t('high') || 'High'}</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('notes_optional') || 'Notes (optional)'}
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            disabled={loading}
          />

          {/* Live total */}
          {(productType === 'existing' ? selectedProduct : (customName && customPrice)) && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="caption" color="text.secondary">
                {t('total_estimated') || 'Total estimated'}
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                €{getTotalPrice()}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>{t('cancel') || 'Cancel'}</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !isValid()}
            startIcon={loading ? <CircularProgress size={18} /> : <FavoriteIcon />}
          >
            {loading ? (t('adding') || 'Adding…') : (t('add_to_wishlist') || 'Add to Wishlist')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Dialog */}
      <AddProductDialog
        open={addProductDialogOpen}
        onClose={() => setAddProductDialogOpen(false)}
        onProductAdded={handleProductAdded}
      />
    </>
  );
};

export default AddToWishlistDialog;
